import React from 'react';
import { cn } from '@/lib/utils';

interface GaugeProps {
  /** 0–100. Out-of-range values clamp. */
  value: number;
  /** Stroke colour for the filled arc. Use the driver-colour palette. */
  colour: string;
  /** Optional explicit size in pixels. Defaults to 84. */
  size?: number;
  className?: string;
}

// Gauge — a 270° radial dial. The arc starts at the 4-o'clock position
// (135° rotation) and sweeps clockwise. Track is muted; fill is the
// driver colour. Centre is left empty so callers can stack a value
// label (the dashboard's Strategic Pulse does this).
//
// Width includes a small padding so the rounded stroke caps don't
// clip at the SVG edge.
export function Gauge({ value, colour, size = 84, className }: GaugeProps) {
  const stroke = 7;
  // Padding constant 9 lands r=34 at the default 84px size — matches the
  // prototype's geometry exactly so the gauge ring sits with the same
  // cap clearance as the spec illustration. Older value (6) drifted to
  // r≈35.5 and pushed the stroke caps a touch close to the SVG edge.
  const r = (size - stroke - 9) / 2;
  const cx = size / 2;
  const C = 2 * Math.PI * r;
  const track = 0.75 * C; // 270° sweep
  const fill = (Math.max(0, Math.min(100, value)) / 100) * track;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={cn('overflow-visible', className)}
      aria-hidden
    >
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        strokeLinecap="round"
        strokeWidth={stroke}
        stroke="hsl(var(--muted-foreground) / 0.15)"
        strokeDasharray={`${track} ${C}`}
        transform={`rotate(135 ${cx} ${cx})`}
      />
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        strokeLinecap="round"
        strokeWidth={stroke}
        stroke={colour}
        strokeDasharray={`${fill} ${C}`}
        transform={`rotate(135 ${cx} ${cx})`}
        style={{ transition: 'stroke-dasharray 0.9s cubic-bezier(0.2, 0, 0, 1)' }}
      />
    </svg>
  );
}

export default Gauge;
